<template>
  <main class="bg-black">
    <div class="relative mt-12 pt-10 c-white" :style="`min-height: calc(100dvh - ${pxToRem(header.height.header)})`">
      <base-picture
        v-for="(image, key) in hero.images"
        :key
        v-bind="image"
        class="animation-fade-in-out w-full op-0"
        :class="{ 'absolute-0': key !== 0 }"
        fit="contain"
        position="top"
        :style="{ animationDelay: `${key * 3}s` }"
      />
      <div
        v-style="{
          inset: { sm: '10vw auto auto 9.33vw', md: '5.07vw auto auto 4.42vw' },
        }"
        class="absolute z-3"
        style="--theme: #FFFFFF"
      >
        <h1
          v-style="{
            'text-size': { sm: '13.33vw', md: '8.43vw' },
            'm': { sm: '0 0 8vw', md: '0 0 2.08vw' },
          }"
          class="w-min uppercase wrap title-1"
          style="line-height: 0.86"
        >
          <base-interpolator :content="hero.title">
            <template #sup="{ args: [message] }">
              <sup v-style:inset="{ sm: '-7.6vw auto auto', md: '-4.7vw auto auto' }" style="font-size: 20%">
                {{ message }}
              </sup>
              <br>
            </template>
          </base-interpolator>
        </h1>
        <h2 v-style:text-size="{ sm: '3.73vw', md: '1.66vw' }" class="flex items-center gap-x-4 uppercase title-1">
          <template v-for="(subtitle, key) in hero.subtitle" :key>
            {{ subtitle }}
            <hr v-if="key + 1 < hero.subtitle.length" v-style:w="{ sm: '13.33vw', md: '5.99vw' }" class="b-white">
          </template>
        </h2>
        <vf-button class="md:hidden hover:c-black" style="margin-top: 4.53vw" to="#product-filter" variant="themable-inverse">
          {{ hero.cta }}
        </vf-button>
      </div>
      <custom-arrow-scroll class="absolute z-2 <md:hidden" style="bottom: 3.33vw; left: 3.125vw" to="#product-filter" />
      <base-picture
        v-style="{
          'inset': { sm: '0 0 auto', md: 'auto 0 0 auto' },
          'max-w': { sm: '100%', md: '45vw' },
        }"
        v-bind="hero.featuredImage"
        class="absolute z-2"
      />
      <div
        v-style="{
          w: { md: '45.83vw' },
          p: { sm: '4.27vw', md: '0' },
          inset: { sm: 'auto 0 35vw', md: 'auto 9.38vw 2.08vw auto' },
        }"
        class="absolute z-3"
      >
        <p class="mb-8 fw-light lh-tight <md:hidden" style="font-size: 1.67vw">
          {{ hero.introCopy }}
        </p>
        <div
          v-style="{
            'gap-x': { sm: '5.86vw', md: '2.08vw' },
            'grid-cols': { sm: 'repeat(3,1fr)' },
          }"
          class="grid md:flex md:between"
        >
          <template v-for="(feature, key) in hero.features" :key>
            <div>
              <h3
                v-style="{
                  'm': { sm: '0 0 4.26vw', md: '0 0 1.25vw' },
                  'p': { sm: '0 0 4.26vw', md: '0' },
                  'text-size': { sm: '3.47vw', md: '1.15vw' },
                }"
                class="lh-none uppercase <md:ws-pre-line <md:b-b <md:b-white display-1"
              >
                {{ feature.title }}
              </h3>
              <p v-style:text-size="{ sm: '3.73vw', md: '0.83vw' }" v-html="feature.description" class="lh-tight md:uppercase" />
            </div>
            <div v-if="key < 2" class="bg-white <md:hidden" style="width: 1px" />
          </template>
        </div>
      </div>
    </div>

    <div id="product-filter" class="my-12 text-center c-white container">
      <h2 v-style:text-size="{ sm: '11.2vw', md: '2vw' }" class="title-1">
        {{ findYourPrefectMatch.title }}
      </h2>
      <p class="mb-8">
        {{ findYourPrefectMatch.subtitle }}
      </p>
      <div v-style:grid-cols="{ lg: 'repeat(5, 1fr)' }" class="center gap-8 lg:grid">
        <hr :class="hrClasses">
        <div v-for="(filter, code, key) in filters" :key class="relative w-full f-col gap-4">
          <h3>
            <sup class="p-2">0{{ key + 1 }}</sup>{{ filter.label }}
          </h3>
          <vf-button
            class="relative mb-5 h-15 flex justify-end duration"
            size="sm"
            variant="primary-inverse"
            @blur="handleBlur(key)"
            @click="filterBySingle(code.toString(), key)"
          >
            <span class="absolute left-1/2 -translate-x-1/2">{{ filters[code].selected.length ? dropDowns[key].label : $t.select }}</span>
            <vf-icon class="flex justify-self-end" :dir="dropDowns[key].isVisible ? 'up' : 'down'" name="chevron" size="lg" />
          </vf-button>
          <transition name="fade">
            <div
              v-if="dropDowns[key].isVisible && (filter.type !== 'Search' && filter.type !== 'PriceInput')"
              class="absolute z-2 w-full f-col gap-2 <md:bg-black"
              style="margin-top: 7rem"
            >
              <base-toggle
                v-for="({ id, label }, i) in filter.options"
                :key="id + i"
                v-model="filters[code].selected"
                class="h-15 flex center b b-white rounded-full bg-black"
                :value="id"
                @update:model-value="filterProducts(code.toString(), key, label)"
              >
                {{ label }}
              </base-toggle>
            </div>
          </transition>
        </div>
        <hr :class="hrClasses">
      </div>
      <div class="my-8 flex between">
        <p class="text-lg">
          {{ productData?.products?.length }} {{ $t.results.other }}
        </p>
        <vf-link class="uppercase" @click="clearFilters">
          {{ findYourPrefectMatch.resetSelection }}
        </vf-link>
      </div>
      <base-grid :cols="{ sm: 2, lg: 3 }" gap="sm">
        <template v-if="pending">
          <div v-for="i in 30" :key="i" class="md:p-10" :style="gridBackgroundStyle">
            <div class="mb-5 skeleton" style="aspect-ratio: 300 / 349" />
            <div class="mx-a mb-5 h-8 w-3/4 skeleton" />
            <div class="mx-a mb-5 h-8 w-1/2 skeleton" />
          </div>
        </template>
        <template v-else>
          <div v-for="({ attributes, name, url }, key) in productData?.products" :key :style="gridBackgroundStyle">
            <div class="f-col center px-4 py-5 md:p-10">
              <span v-style:m="{ sm: '0 0 8.26vw', md: '0 0 1.35vw' }" class="relative">
                <base-link :to="url">
                  <base-picture v-bind="findYourPrefectMatch.productBackgroundImage" class="absolute-0" />
                  <base-picture
                    :alt="`${name}`"
                    class="relative z-1"
                    :src="`https://assets.thenorthface.eu/image/upload/q_auto,f_auto/v1725523825/${attributes[0]?.options[0]?.id}-TBG`"
                  />
                </base-link>
              </span>
              <h3 v-style:m="{ sm: '0 0 9.86vw', md: '0 0 1.35vw' }" class="title-3 lh-none">
                {{ name }}
              </h3>
              <vf-button class="<md:w-full" size="sm" :to="url" variant="primary-inverse">
                {{ $t.shopNow }}
              </vf-button>
            </div>
          </div>
        </template>
      </base-grid>
    </div>

    <div class="relative w-full center c-white">
      <base-picture class="w-full" fit="contain" v-bind="aniBackground" />
      <h2
        v-style="{
          'text-size': { sm: '3.73vw', md: '1.66vw' },
          'inset': { sm: '6rem auto auto 50%', md: '2.25rem auto auto 50%' },
        }"
        class="absolute flex items-center gap-x-4 uppercase -translate-x-1/2 title-1"
      >
        <template v-for="(subtitle, key) in hero.subtitle" :key>
          {{ subtitle }}
          <hr v-if="key + 1 < hero.subtitle.length" v-style:w="{ sm: '13.33vw', md: '5.99vw' }" class="b-white">
        </template>
      </h2>
      <div v-style:inset="{ sm: '6.5rem 0 0 0', md: '2.5rem 0 0 0' }" class="absolute">
        <div v-style:text-size="{ sm: '6.4vw', md: '3.38vw' }" class="absolute grid mt-16 w-full lh-none uppercase md:cols-3 -mb-4">
          <div class="relative <md:hidden">
            <h1 v-html="outerTitle" class="animation-delayed-fade-out absolute left-1/2 top-1/2 fw-light -translate-1/2" />
            <h1 v-html="triclimateTitle" class="animation-delayed-fade-in absolute left-1/2 top-1/2 fw-medium op-0 -translate-1/2" />
          </div>
          <div v-style:h="{ sm: '64vw', md: '31vw' }" class="relative flex justify-center">
            <div class="animation-delayed-fade-in mx-a h-full op-0">
              <base-picture class="h-full" v-bind="jacketImage" />
            </div>
            <div class="animation-slide-in-left absolute bottom-0 top-0 z-2">
              <base-picture class="h-full" v-bind="outerJacketImage" />
            </div>
            <div class="animation-slide-in-right absolute bottom-0 top-0 z-2">
              <base-picture class="h-full" v-bind="innerJacketImage" />
            </div>
          </div>
          <div class="relative <md:hidden">
            <h1 v-html="innerTitle" class="animation-delayed-fade-out absolute left-1/2 top-1/2 block text-right fw-light -translate-1/2" />
            <h1 v-html="jacketsTitle" class="animation-delayed-fade-in absolute left-1/2 top-1/2 flex fw-medium op-0 -translate-x-1/2 md:-translate-1/2" />
          </div>
          <div class="relative mt-10 text-center md:hidden">
            <h1 v-html="triclimateJacketsTitle" class="animation-delayed-fade-in w-full fw-medium" />
            <div class="flex justify-around fw-light" style="margin-top: -3rem">
              <h1 v-html="innerTitle" class="animation-delayed-fade-out" />
              <h1 v-html="outerTitle" class="animation-delayed-fade-out" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Benefit section -->
    <section
      v-style:p="{ sm: '10vw 6vw', md: '4vw 6vw' }"
      class="bg-grey-80 text-center lh-tight"
    >
      <h2
        v-style="{
          'm': { sm: '0 0 4vw', md: '0 0 1vw' },
          'text-size': { sm: '11vw', md: '3.25vw' },
        }"
        class="fw-medium"
      >
        {{ benefits.title }}
      </h2>
      <p
        v-style="{
          'p': { sm: '0', md: '0 7vw' },
          'm': { sm: '0 0 10vw', md: '0 0 4vw' },
          'text-size': { sm: '5vw', md: '1.2vw' },
        }"
      >
        {{ benefits.paragraph }}
      </p>
      <div
        v-style:m="{ sm: '0 0 10vw', md: '0 0 4vw' }"
        class="w-full md:flex"
      >
        <div
          v-for="({ image, icon, paragraph, title }, key) in benefits.items"
          :key
          class="flex center <md:mb-6 md:w-1/3 md:f-col"
          :class="{ 'md:mr-12': key !== benefits.items.length - 1 }"
        >
          <div class="relative <md:w-1/2">
            <base-picture v-bind="image" />
            <div class="absolute-0 f-col center">
              <base-picture v-bind="icon" class="mb-3" style="width:35%" />
              <p class="c-white fw-medium uppercase">
                {{ title }}
              </p>
            </div>
          </div>
          <p v-style:text-size="{ sm: '3.75vw', md: '.9vw' }" class="md:mt-6 <md:w-1/2 <md:pl-4 <md:text-left">
            {{ paragraph }}
          </p>
        </div>
      </div>
      <div class="<md:f-col <md:space-y-3 md:space-x-3">
        <vf-button v-for="({ title, to }, key) in benefits.buttons" :key :to>
          {{ $t[title] }}
        </vf-button>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import type { ProductCatalogQuery } from '#types/product'

const { $t } = useNuxtApp()
definePageMeta({ header: { transparent: true } })

const { data, error } = await useApi().internal.getStaticPage('triclimate-jackets')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { defaultSort } = useAppConfig().pages.plp
const route = useRoute()
const sort = useRouteQuery('sort', defaultSort)
const filtersParam = useRouteQuery<string>('filters', '')
const filtersQuery = computed(() => getFiltersQueryString(filtersParam.value))
const hrClasses = 'mt-3 w-full b-white <lg:hidden'
const gridBackgroundStyle = 'background: #191919'

const dropDowns = ref([
  { id: 0, isVisible: false, label: '' },
  { id: 1, isVisible: false, label: '' },
  { id: 2, isVisible: false, label: '' }
])

const query = reactive<ProductCatalogQuery>({
  cgid: '798802',
  sort: sort.value,
  start: 0,
  count: 30,
  filters: filtersQuery.value,
  ...(route.query.customTrigger ? { customTrigger: route.query.customTrigger as string } : {})
})

const { pending, data: productData, error: productError } = await useApi().products.catalog(query, {
  lazy: true,
  key: '798802',
  transform: (input): typeof input => ({
    ...input,
    filters: getUrlSafeFilters(input.filters),
  }),
})

if (productError.value)
  throw createError({ statusCode: productError.value.statusCode! >= 500 ? 500 : 404 })

const {
  filters,
  filterBy,
  clearFilters,
  removeFilter
} = useFilters(
  () => productData.value?.filters || []
)

watch(filtersParam, () => {
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
})

const filterBySingle = (code: string, key: number) => {
  if (filters.value[code]?.selected?.length) {
    const id = filters.value[code]?.selected[0]
    removeFilter({ code, id })
  }
  dropDowns.value[key].isVisible = !dropDowns.value[key].isVisible
}

const filterProducts = (code: string, key: number, label: string) => {
  filterBy(code)
  dropDowns.value[key] = {
    id: key,
    isVisible: !dropDowns.value[key].isVisible,
    label
  }
}

const handleBlur = (key: number) => {
  setTimeout(() => dropDowns.value[key].isVisible = false, 250)
}

const {
  hero,
  findYourPrefectMatch,
  aniBanner: {
    aniBackground,
    innerTitle,
    jacketsTitle,
    outerTitle,
    triclimateTitle,
    triclimateJacketsTitle,
    jacketImage,
    innerJacketImage,
    outerJacketImage
  },
  benefits
} = data.value

const header = useHeaderStore()
</script>
